import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@organizacaox/lai-design-system/router";
import { useSession } from "@/lib/auth-client";

import { clearTempPassword } from "@/lib/pending-temp-password";
import { onSessionExpired } from "@/lib/session-expiration";
import { usePreferencesStore } from "@/preferences-store";
import { useSessionGuardClearSession } from "@/hooks/auth-actions";
import { useAuthRefreshAuthSession } from "@/hooks/auth/refresh-auth-session";

// Montado só nas rotas autenticadas: não interfere em login, convite ou reset.
export function SessionGuard({ children }: { children: ReactNode }) {
  const runSessionGuardClearSession = useSessionGuardClearSession();
  const { mutateAsync: runAuthRefreshAuthSession } = useAuthRefreshAuthSession();
  const auth = useSession();
  const navigate = useNavigate();
  const leaving = useRef(false);
  const [expired, setExpired] = useState(false);
  const expire = useCallback(() => {
    if (leaving.current) return;
    leaving.current = true;
    setExpired(true);
    clearTempPassword();
    usePreferencesStore.getState().setViewAs(null);
    runSessionGuardClearSession();
    void navigate({ to: "/auth", replace: true });
  }, [navigate]);

  useEffect(() => onSessionExpired(expire), [expire]);
  useEffect(() => {
    if (!auth.isPending && (auth.error?.status === 401 || (!auth.error && !auth.data?.session)))
      expire();
  }, [auth.isPending, auth.error, auth.data, expire]);

  const expiresAt = auth.data?.session.expiresAt?.getTime();
  useEffect(() => {
    if (!expiresAt || !Number.isFinite(expiresAt)) return;
    // Confere no servidor: uma sessão renovada em outra aba pode continuar válida.
    const timer = setTimeout(
      () => {
        void runAuthRefreshAuthSession([]);
      },
      Math.min(Math.max(expiresAt - Date.now(), 0), 2_147_483_647),
    );
    return () => clearTimeout(timer);
  }, [expiresAt]);

  return expired ? null : children;
}
